'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOptions {
  categories: string[];
  priceRange: {
    min: number;
    max: number;
  };
  sortBy: 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc' | 'popularity';
  inStockOnly: boolean;
}

interface FilterSidebarProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  availableCategories: Array<{ id: string; name: string; count: number }>;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

const FilterSidebar = ({
  filters,
  onFilterChange,
  availableCategories,
  isMobileOpen,
  onMobileClose,
}: FilterSidebarProps) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);

  const handleCategoryToggle = (categoryId: string) => {
    const newCategories = localFilters.categories.includes(categoryId)
      ? localFilters.categories.filter((c) => c !== categoryId)
      : [...localFilters.categories, categoryId];

    const updated = { ...localFilters, categories: newCategories };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handlePriceChange = (type: 'min' | 'max', value: string) => {
    const numValue = parseInt(value) || 0;
    const updated = {
      ...localFilters,
      priceRange: {
        ...localFilters.priceRange,
        [type]: numValue,
      },
    };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleSortChange = (sortBy: FilterOptions['sortBy']) => {
    const updated = { ...localFilters, sortBy };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleStockToggle = () => {
    const updated = { ...localFilters, inStockOnly: !localFilters.inStockOnly };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const handleReset = () => {
    const resetFilters: FilterOptions = {
      categories: [],
      priceRange: { min: 0, max: 100000 },
      sortBy: 'popularity',
      inStockOnly: false,
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  const sortOptions = [
    { value: 'popularity', label: 'По популярности' },
    { value: 'price-asc', label: 'Цена: по возрастанию' },
    { value: 'price-desc', label: 'Цена: по убыванию' },
    { value: 'name-asc', label: 'Название: А-Я' },
    { value: 'name-desc', label: 'Название: Я-А' },
  ];

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border p-4 lg:hidden">
        <h2 className="font-heading text-lg font-semibold text-text-primary">Фильтры</h2>
        <button
          onClick={onMobileClose}
          className="flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors duration-250 hover:bg-muted"
          aria-label="Закрыть фильтры"
        >
          <Icon name="XMarkIcon" size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-text-primary">
            Сортировка
          </h3>
          <div className="space-y-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSortChange(option.value as FilterOptions['sortBy'])}
                className={`w-full rounded-md px-3 py-2 text-left font-caption text-sm transition-all duration-250 ${
                  localFilters.sortBy === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-text-primary hover:bg-muted/80'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 border-t border-border pt-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-text-primary">Категории</h3>
          <div className="space-y-2">
            {availableCategories.map((category) => (
              <label
                key={category.id}
                className="flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors duration-250 hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={localFilters.categories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="h-4 w-4 rounded border-border text-accent focus:ring-2 focus:ring-accent focus:ring-offset-2"
                />
                <span className="flex-1 font-caption text-sm text-text-primary">
                  {category.name}
                </span>
                <span className="font-caption text-xs text-text-secondary">{category.count}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-6 border-t border-border pt-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-text-primary">
            Диапазон цен (₸)
          </h3>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block font-caption text-xs text-text-secondary">От:</label>
              <input
                type="number"
                value={localFilters.priceRange.min}
                onChange={(e) => handlePriceChange('min', e.target.value)}
                min={0}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="mb-1 block font-caption text-xs text-text-secondary">До:</label>
              <input
                type="number"
                value={localFilters.priceRange.max}
                onChange={(e) => handlePriceChange('max', e.target.value)}
                min={0}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
        </div>

        <div className="mb-6 border-t border-border pt-6">
          <label className="flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors duration-250 hover:bg-muted">
            <input
              type="checkbox"
              checked={localFilters.inStockOnly}
              onChange={handleStockToggle}
              className="h-4 w-4 rounded border-border text-accent focus:ring-2 focus:ring-accent focus:ring-offset-2"
            />
            <span className="font-caption text-sm text-text-primary">Только в наличии</span>
          </label>
        </div>
      </div>

      <div className="border-t border-border p-4">
        <button
          onClick={handleReset}
          className="w-full rounded-md bg-muted px-4 py-3 font-heading text-sm font-medium text-text-primary transition-all duration-250 hover:bg-muted/80 active:scale-95"
        >
          Сбросить фильтры
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block lg:w-80">
        <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-lg bg-card shadow-sm">
          {sidebarContent}
        </div>
      </aside>

      {isMobileOpen && (
        <>
          <div
            className="fixed inset-0 z-[200] bg-background/80 lg:hidden"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          <aside className="fixed left-0 top-0 z-[200] h-full w-full max-w-sm bg-card shadow-xl lg:hidden">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
};

export default FilterSidebar;
